import React, { useContext, useState } from "react";
import { PageContext } from "./App";
import Countdown from "./components/Countdown";
import Gauge from "./components/Gauge";
import ProgressBar from "@ramonak/react-progress-bar";
import CodeSelect from "./components/CodeSelect";
import { Clock } from "lucide-react";
import { navigationController } from "./utils/navigationControl";

// RENDERS THE MAIN PAGE
const Main = () => {
  const [pendingShiftEnd, setPendingShiftEnd] = useState("");
  const [pendingDTDuration, setPendingDTDuration] = useState("");

  const {
    warningEventsCurrent,
    sendToSiteWiseShift,
    estimatedFinishColor,
    estimatedFinishTime,
    plannedFinishTime,
    totalAutoDowntime,
    DTAutoEvents,
    DTManualEvents,
    progressPercentage,
    setShiftPage,
    now,
    formattedDateTime,
    timerKey,
    setTimerKey,
    downtime,
    nowISOString,
    shiftStart,
    shiftEnd,
    setEndTime,
    countdownInitialTime,
    duration,
    changeoverMode,
    setCountdownInitialTime,
    setManualOvertime,
    setChangeoverMode,
    counterMaxTime,
    currentMode,
    valuesSW,
    totalDowntime,
    pendingStartTime,
    setPendingStartTime,
    pendingEndTime,
    setPendingEndTime,
    pendingCode,
    setPendingCode,
    pendingComment,
    setPendingComment,
    setShowPopup,
    showPopup,
    setStartTime,
    setCode,
    setComment,
    setDowntime,
    sendToSiteWise,
    sendToSiteWiseDT,
    productionPlan,
    site,
    productLine,
    batchID,
  } = useContext(PageContext);

  // Prevent back navigation from main page
  React.useEffect(() => {
    // Ensure user can't go back to sign-in page
    navigationController.preventBackNavigation();
  }, []);

  // The view is divided to 9 parts or cells.
  return (
    <div className="fullscreen-container">
      <div className="app-container">
        <div className="grid">
          <div className="row row-1">
            {/* CELL 1 - TOP LEFT - DETAILS LABEL */}

            <div className="details-label">
              <span>
                {formattedDateTime}&nbsp;&nbsp;|&nbsp;&nbsp;{site}
                &nbsp;&nbsp;|&nbsp;&nbsp;{productLine}
                &nbsp;&nbsp;|&nbsp;&nbsp;Batch:{batchID}
              </span>
            </div>

            {/* CELL 2 - TOP MIDDLE - PRODUCT NAME  */}

            <div className="top-cell">
              <div className="label">
                <div className="title">Current Product</div>
                <div className="highlight">
                  {" "}
                  {productionPlan.length > 0
                    ? productionPlan[0].product_description
                    : "No product loaded"}
                </div>
              </div>
            </div>

            {/* CELL 3 - TOP RIGHT - EMPTY  */}

            {/* <div className="cell"></div> */}
          </div>
          <div className="row row-2">
            {/* CELL 4 - MID LEFT - TOTAL DOWNTIME DURATIONS  */}

            <div className="cell">
              <div className="label-side-downtime">
                <div>Auto Downtime Total:</div>
                <div className="highlight-side">
                  {Math.floor(totalAutoDowntime / 60) > 0
                    ? `${Math.floor(totalAutoDowntime / 60)}m ${
                        totalAutoDowntime % 60
                      }s`
                    : `${totalAutoDowntime % 60}s`}
                </div>
              </div>

              <div className="label-side-downtime">
                <div>Manual Downtime Total:</div>
                <div className="highlight-side">{totalDowntime}m</div>
              </div>

              <div style={{ position: "relative" }}>
                <button
                  className="button-inline"
                  onClick={async () => {
                    setShowPopup(true);

                    setPendingStartTime("");  // clear the settings
                    setPendingEndTime("");
                    setPendingCode("");
                    setPendingComment("");
                    setPendingDTDuration("");
                  }}
                >
                  Add Downtime
                  <Clock size={18} />
                </button>

                  {/* POPUP FOR DOWNTIME */}
                {showPopup && (
                   <>
                  <div className="backdrop-blur" /> 
                  
                  <div className="popup-overlay">
                    <div className="popup-inline">
                      <div className="popup-content">
                        <div className="input-row">
                          <label>Enter Start Time:</label>
                          <div className="input-right">
                            <input
                              type="datetime-local"
                              className="datetime-input"
                              style={{ marginTop: "6px" }}
                              value={pendingStartTime}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPendingStartTime(value);

                                const mins = parseInt(pendingDTDuration, 10);

                                if (value && !isNaN(mins)) {
                                  const start = new Date(value);
                                  const newEnd = new Date(
                                    start.getTime() + mins * 60000
                                  );
                                  const pad = (n) => String(n).padStart(2, "0");
                                  const isoLocal = `${newEnd.getFullYear()}-${pad(
                                    newEnd.getMonth() + 1
                                  )}-${pad(newEnd.getDate())}T${pad(
                                    newEnd.getHours()
                                  )}:${pad(newEnd.getMinutes())}`;
                                  setPendingEndTime(isoLocal);
                                }

                                if (!pendingDTDuration && pendingEndTime) {
                                  setPendingEndTime(value);
                                }
                                if (value > new Date()) {
                                  const offset = now.getTimezoneOffset();
                                  const localDate = new Date(
                                    now.getTime() - offset * 60 * 1000
                                  );
                                  const isoString = localDate
                                    .toISOString()
                                    .slice(0, 16);
                                  e.target.value = isoString;
                                  setPendingStartTime(isoString);
                                  pendingDTDuration && setPendingDTDuration("");
                                  pendingEndTime &&
                                    setPendingEndTime(isoString);
                                }
                              }}
                              max={nowISOString}
                              onBlur={(e) => {
                                const value = new Date(e.target.value);
                                if (value > new Date()) {
                                  alert(
                                    "You selected a future time. Please choose a valid past time."
                                  );
                                  const offset = now.getTimezoneOffset();
                                  const localDate = new Date(
                                    now.getTime() - offset * 60 * 1000
                                  );
                                  const isoString = localDate
                                    .toISOString()
                                    .slice(0, 16);
                                  e.target.value = isoString;
                                  setPendingStartTime(isoString);
                                  pendingDTDuration && setPendingDTDuration("");
                                  pendingEndTime &&
                                    setPendingEndTime(isoString);
                                }
                              }}
                            />
                          </div>
                        </div>

                        <div className="input-row">
                          <label>Duration:</label>

                          <div className="input-right">
                            <input
                              type="number"
                              className="duration-input"
                              // placeholder="Enter duration (mins)"
                              value={pendingDTDuration}
                              onChange={(e) => {
                                const mins = parseInt(e.target.value, 10);
                                setPendingDTDuration(mins);

                                if (pendingStartTime && !isNaN(mins)) {
                                  const start = new Date(pendingStartTime);
                                  let newEnd = new Date(
                                    start.getTime() + mins * 60000
                                  );

                                  if (newEnd > new Date()) {
                                    newEnd = new Date();
                                    const newMins = Math.floor(
                                      (newEnd - start) / 60000
                                    );
                                    setPendingDTDuration(newMins);
                                  }

                                  const pad = (n) => String(n).padStart(2, "0");
                                  const isoLocal = `${newEnd.getFullYear()}-${pad(
                                    newEnd.getMonth() + 1
                                  )}-${pad(newEnd.getDate())}T${pad(
                                    newEnd.getHours()
                                  )}:${pad(newEnd.getMinutes())}`;

                                  setPendingEndTime(isoLocal);
                                } else if (pendingEndTime && !isNaN(mins)) {
                                  const end = new Date(pendingEndTime);
                                  const newStart = new Date(
                                    end.getTime() - mins * 60000
                                  );

                                  const pad = (n) => String(n).padStart(2, "0");
                                  const isoLocal = `${newStart.getFullYear()}-${pad(
                                    newStart.getMonth() + 1
                                  )}-${pad(newStart.getDate())}T${pad(
                                    newStart.getHours()
                                  )}:${pad(newStart.getMinutes())}`;

                                  setPendingStartTime(isoLocal);
                                }
                              }}
                            />
                            <label>Minutes</label>
                          </div>
                        </div>

                        <div className="input-row">
                          <label>Enter End Time:</label>
                          <div className="input-right">
                            <input
                              type="datetime-local"
                              className="datetime-input"
                              style={{ marginTop: "6px" }}
                              value={pendingEndTime}
                              onChange={(e) => {
                                const value = e.target.value;
                                const end = new Date(value);

                                const offset = now.getTimezoneOffset();
                                const localDate = new Date(
                                  now.getTime() - offset * 60 * 1000
                                );
                                const isoString = localDate
                                  .toISOString()
                                  .slice(0, 16);

                                if (end > new Date()) {
                                  e.target.value = isoString;
                                  setPendingEndTime(isoString);
                                  setPendingStartTime(isoString);
                                  setPendingDTDuration("");
                                } else {
                                  setPendingEndTime(value);

                                  if (pendingDTDuration) {
                                    const newStart = new Date(
                                      end.getTime() - pendingDTDuration * 60000
                                    );
                                    const pad = (n) =>
                                      String(n).padStart(2, "0");
                                    const isoLocal = `${newStart.getFullYear()}-${pad(
                                      newStart.getMonth() + 1
                                    )}-${pad(newStart.getDate())}T${pad(
                                      newStart.getHours()
                                    )}:${pad(newStart.getMinutes())}`;
                                    setPendingStartTime(isoLocal);
                                  } else if (pendingStartTime) {
                                    const start = new Date(pendingStartTime);
                                    const end = new Date(value);
                                    const diffInMinutes = Math.floor(
                                      (end - start) / 60000
                                    );

                                    if (
                                      !isNaN(diffInMinutes) &&
                                      diffInMinutes >= 0 &&
                                      end < new Date()
                                    ) {
                                      setPendingDTDuration(diffInMinutes);
                                    }
                                  }
                                }
                              }}
                              max={nowISOString}
                              onBlur={(e) => {
                                const value = new Date(e.target.value);
                                const offset = now.getTimezoneOffset();
                                const localDate = new Date(
                                  now.getTime() - offset * 60 * 1000
                                );
                                const isoString = localDate
                                  .toISOString()
                                  .slice(0, 16);

                                if (value > new Date()) {
                                  alert(
                                    "You selected a future time. Please choose a valid past time."
                                  );

                                  e.target.value = isoString;
                                  setPendingEndTime(isoString);
                                  setPendingStartTime(isoString);
                                  setPendingDTDuration("");
                                } else if (pendingStartTime) {
                                  const start = new Date(pendingStartTime);
                                  if (start > value) {
                                    setPendingEndTime(isoString);
                                    setPendingStartTime(isoString);
                                    setPendingDTDuration("");
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                        <div className="input-row"></div>
                        <div className="input-row">
                          <label>Select Reason Code:</label>
                        </div>

                        <div className="input-row">
                          <CodeSelect
                            value={pendingCode}
                            onChange={setPendingCode}
                          />
                        </div>

                        <div
                          className="input-row input-textarea-row"
                          style={{
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <label>Comments:</label>
                          <textarea
                            value={pendingComment}
                            onChange={(e) => setPendingComment(e.target.value)}
                            placeholder="Enter text here.."
                            className="popup-textarea"
                            rows={3}
                          />
                        </div>
                        <div className="popup-buttons">
                          <button
                            style={{ backgroundColor: "#eeeeee" }}
                            onClick={() => setShowPopup(false)}
                          >
                            Cancel
                          </button>
                          <button
                            style={{ backgroundColor: "#93C2E4" }}
                            onClick={async () => {
                              if (
                                pendingStartTime &&
                                pendingStartTime &&
                                pendingCode
                              ) {
                                if (pendingEndTime < pendingStartTime) {
                                  alert(
                                    "Shift end cannot be earlier than the shift start."
                                  );
                                  return;
                                }

                                setStartTime(pendingStartTime);
                                setEndTime(pendingEndTime);
                                setCode(pendingCode);
                                setComment(pendingComment);

                                const diffInSeconds = Math.floor(
                                  (new Date(pendingEndTime) -
                                    new Date(pendingStartTime)) /
                                    1000 /
                                    60
                                );

                                setDowntime(diffInSeconds);

                                console.log("downtime: ", downtime);
                                console.log(
                                  "pendingStartTime: ",
                                  pendingStartTime
                                );
                                console.log("pendingEndTime: ", pendingEndTime);

                                await sendToSiteWiseDT({
                                  startTime: pendingStartTime,
                                  endTime: pendingEndTime,
                                  code: pendingCode,
                                  comment: pendingComment,
                                });

                                setShowPopup(false);
                              } else {
                                alert(
                                  "Please enter start, end times and reason code."
                                );
                              }
                            }}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                   </>        
                )}
              </div>
            </div>

            {/* CELL 5 - MID MIDDLE - GAUGE OR COUNTDOWN TIMER DEPENDING ON CHANGEOVER, STATUS, PROGRESS BAR AND WARNING*/}

            <div className="cell">
              <div className="inner-row">
                {/* CELL 5.1  - GAUGE OR COUNTDOWN TIMER*/}
                <div className="inner-cell wide">
                  {changeoverMode !== null ? (
                    valuesSW.changeover == "true" ? (
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Countdown
                          key={`${timerKey}-${changeoverMode}`}
                          duration={duration}
                          initial={countdownInitialTime}
                          isOvertime={changeoverMode}
                          onComplete={() => {
                            if (
                              valuesSW.changeover == "true" &&
                              changeoverMode === false
                            ) {
                              setCountdownInitialTime(counterMaxTime); // max value of the changeover downtime
                              setManualOvertime(true);
                              setChangeoverMode(true);
                              setTimerKey((prev) => prev + 1);
                              return { shouldRepeat: false };
                            }
                            // Already in overtime: keep repeating
                            if (
                              valuesSW.changeover === "true" &&
                              changeoverMode === true
                            ) {
                              // TODO: display round 2 or something.
                              return { shouldRepeat: true };
                            }
                            return { shouldRepeat: false };
                          }}
                          counterMaxTime={counterMaxTime}
                        />
                      </div>
                    ) : (
                      <Gauge value={valuesSW.speed} />
                    )
                  ) : (
                    <div>Loading...</div>
                  )}
                </div>

                {/* CELL 5.2 - STATUS*/}
                <div className="inner-cell">
                  <div>Status:</div>
                  <div
                    className={`status${
                      valuesSW.currentMode !== 0 ? " green-status" : ""
                    }`}
                  >
                    {currentMode}
                  </div>
                    {/* WARNING TEXT*/}
                  {warningEventsCurrent &&
                    warningEventsCurrent !== "No warnings" && (
                      <span className="warning">⚠️ {warningEventsCurrent}</span>
                    )}
                </div>
                {/* CELL 5.3 - PROGRESS*/}

                <div style={{ marginTop: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "14px",
                    }}
                  >
                    {progressPercentage < 7 && (
                      <>
                        <span>Progress: </span>
                        <span>{progressPercentage}%</span>
                      </>
                    )}
                  </div>
                  <ProgressBar
                    completed={progressPercentage} // {progressPercentage}
                    maxCompleted={100}
                    bgColor="#474545"
                    baseBgColor="#c0c0c0"
                    height="30px"
                    borderRadius="1px"
                    labelColor={
                      progressPercentage >= 7 ? "#ffffff" : "transparent"
                    }
                  />
                </div>
              </div>
            </div>

            {/* CELL 6 - MID RIGHT - TOP EVENTS  */}
            <div className="cell">
              <div className="label-side">
                <div>Top Auto Downtime Events:</div>

                {Array.isArray(DTAutoEvents) && DTAutoEvents.length > 0 ? (
                  <div
                    className="losses-text"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {DTAutoEvents.sort((a, b) => b.seconds - a.seconds)
                      .slice(0, 3)
                      .map(({ label, seconds }) => {
                        const mins = Math.floor(seconds / 60);
                        const secs = seconds % 60;
                        return (
                          <div key={label} className="highlight-side with-tick">
                            - {label}: {mins > 0 ? `${mins}m ` : ""}
                            {secs}s
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="losses-text">
                    <div className="highlight-side with-tick">
                      - No events yet
                    </div>
                  </div>
                )}
              </div>

              <div className="label-side">
                <div>Top Manual Downtime Events:</div>

                {Array.isArray(DTManualEvents) && DTManualEvents.length > 0 ? (
                  <div
                    className="losses-text"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {DTManualEvents.sort((a, b) => b.seconds - a.seconds)
                      .slice(0, 3)
                      .map(({ label, seconds }) => {
                        const mins = Math.floor(seconds / 60);
                        const secs = seconds % 60;
                        return (
                          <div key={label} className="highlight-side with-tick">
                            - {label}: {mins > 0 ? `${mins}m ` : ""}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="losses-text">
                    <div className="highlight-side with-tick">
                      - No events yet
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          <div className="row row-3">
            {/* CELL 7 - BOTTOM LEFT - SHIFT DETAILS  */}
           
            <div
              className="cell"
              style={{ flex: 1, alignItems: "flex-start", padding: "1rem" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  fontSize: "1.2rem",
                }}
              >
                {shiftStart && shiftStart !== "test" && (
                  <div>
                    <strong>Shift Started:</strong>{" "}
                    {new Date(shiftStart).toLocaleTimeString("en-AU", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                    })}
                  </div>
                )}

                {shiftEnd &&
                  new Date(shiftStart) < new Date(shiftEnd) &&
                  !isNaN(new Date(shiftEnd).getTime()) && (
                    <div>
                      <strong>Shift Ended:</strong>{" "}
                      {new Date(shiftEnd).toLocaleTimeString("en-AU", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      })}
                    </div>
                  )}

                {(!shiftEnd || new Date(shiftStart) >= new Date(shiftEnd)) && (
                  <>
                    <div>
                      <label style={{ display: "block", marginBottom: "4px" }}>
                        Enter Shift End:
                      </label>

                      <input
                        type="datetime-local"
                        className="datetime-input"
                        style={{ marginTop: "6px" }}
                        value={pendingShiftEnd}
                        onChange={(e) => setPendingShiftEnd(e.target.value)}
                        max={nowISOString}
                        onBlur={(e) => {
                          const value = new Date(e.target.value);
                          if (value > new Date()) {
                            alert(
                              "You selected a future time. Please choose a valid past time."
                            );
                            const offset = now.getTimezoneOffset();
                            const localDate = new Date(
                              now.getTime() - offset * 60 * 1000
                            );
                            const isoString = localDate
                              .toISOString()
                              .slice(0, 16);

                            e.target.value = isoString;
                            setPendingShiftEnd(isoString); 
                          }
                          if (value < new Date(shiftStart)) {
                            alert(
                              "Shift end cannot be earlier than the shift start."
                            );
                            const offset = now.getTimezoneOffset();
                            const localDate = new Date(
                              now.getTime() - offset * 60 * 1000
                            );
                            const isoString = localDate
                              .toISOString()
                              .slice(0, 16);

                            e.target.value = isoString;
                            setPendingShiftEnd(isoString); 
                          }
                        }}
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <button
                        style={{ width: "fit-content" }}
                        onClick={async () => {
                          if (pendingShiftEnd) {
                            if (
                              new Date(pendingShiftEnd) < new Date(shiftStart)
                            ) {
                              alert(
                                "Shift end cannot be earlier than the shift start."
                              );
                              return;
                            }

                            await sendToSiteWise({
                              endShiftTime: pendingShiftEnd.toString(),
                            });

                            const payload = {
                              endShiftTime: pendingShiftEnd,
                            };

                            await sendToSiteWiseShift(payload);
                          } else {
                            alert("Please enter an end time.");
                          }
                        }}
                      >
                        Submit End Time
                      </button>
                    </div>
                  </>
                )}

                {new Date(shiftStart) < new Date(shiftEnd) && (
                  <button
                    style={{ width: "fit-content" }}
                    onClick={async () => {
                      setShiftPage(true);
                    }}
                  >
                    Enter New Shift
                  </button>
                )}
              </div>
            </div>

            {/* CELL 8 - BOTTOM MIDDLE - THAT EXTENDS TO BOTTOM RIGHT  */}
            <div className="cell" style={{ flex: 2 }}>
              <div className="bottom-label">
                <div>
                  Planned Finish Time:
                  <span className="time"> {plannedFinishTime || " N/A"}</span>
                </div>
                <div>
                  Current Finish Time:
                  <span
                    className="time"
                    style={{ color: estimatedFinishColor }}
                  >
                    {" "}
                    {estimatedFinishTime || " N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* CELL 9 - BOTTOM RIGHT - NOT USED  */}
            {/* <div className="cell"></div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Main;
